import { useState } from 'react'
import { HelpCircle, Plus, X, Info as InfoIcon } from 'lucide-react'
import { useScopeStore } from '../store'
import { INTERNATIONAL_KYB_PRODUCTS } from '../types'
import type { WorkflowStepType, BuilderWorkflow, ComplexWorkflow } from '../types'
import { getFieldById, getGroupById, getSectionById } from '../schema'
import { Tooltip } from '../../../components/ui/Tooltip'
import { ResellerLockBadge, useIsResellerLocked, useHasResellerParent, ResellerInheritedBanner } from './ResellerLock'
import { useUpsellContext } from '../hooks/useUpsellContext'

type TriState = 'yes' | 'no' | 'unknown'

function TriStateRadio({
  name,
  value,
  onChange,
  disabled,
}: {
  name: string
  value: TriState
  onChange: (next: TriState) => void
  disabled: boolean
}) {
  const { mapLabel } = useUpsellContext()
  const options: { value: TriState; label: string }[] = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: mapLabel("Don't know") },
  ]

  return (
    <div className="inline-flex gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`px-3 py-1.5 text-xs rounded-full border cursor-pointer transition-colors ${
            value === opt.value
              ? opt.value === 'unknown'
                ? 'border-amber-500 bg-amber-500/15 text-amber-400 font-medium'
                : 'border-primary bg-primary/10 text-[var(--text-primary)]'
              : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-600'
          } ${disabled ? (value === opt.value ? 'cursor-not-allowed' : 'opacity-40 cursor-not-allowed') : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            disabled={disabled}
            className="sr-only"
          />
          {opt.value === 'unknown' && value === 'unknown' ? `⚠ ${opt.label}` : opt.label}
        </label>
      ))}
    </div>
  )
}

const AU_CHECKS = [
  { id: 'amlStandard', label: 'AML Standard (PEPs, Sanctions)' },
  { id: 'amlEnhanced', label: 'AML Enhanced (Standard + Adverse Media)' },
  { id: 'asicInfo', label: 'ASIC Information (Current)' },
  { id: 'ubo', label: 'UBO (Ultimate Beneficial Ownership)' },
  { id: 'creditReport', label: 'Business Credit Report (CreditorWatch)' },
]


const TRUST_TYPE_SPLIT_FIELDS: Array<{ key: keyof NonNullable<ReturnType<typeof useScopeStore.getState>['scope']['kybConfig']['trustTypeSplit']>; label: string }> = [
  { key: 'unitPercent', label: 'Unit Trusts' },
  { key: 'discretionaryPercent', label: 'Discretionary Trusts' },
  { key: 'smsfPercent', label: 'SMSF (Self-Managed Super Fund)' },
  { key: 'otherPercent', label: 'Other Trust Types' },
]

const STEP_TYPE_LABELS: Record<WorkflowStepType, string> = {
  blocklist: 'Blocklist',
  gov_id: 'Gov ID',
  kyc: 'KYC',
  death_check: 'AU Death Check',
  idv_biometrics: 'IDV/Biometrics',
  aml: 'AML',
  fraud: 'Fraud',
  risk: 'Risk',
  duplicate: 'Duplicate Check',
}

function WorkflowPreview({ workflowId, workflows }: { workflowId: string; workflows: ReturnType<typeof useScopeStore.getState>['scope']['workflowsConfig'] }) {
  const workflow = workflows.find((wf) => wf.id === workflowId)
  if (!workflow) return null

  // Complex workflow — show name + note
  if (workflow.kind === 'complex') {
    const cw = workflow as ComplexWorkflow
    return (
      <div className="mt-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3">
        <div className="text-xs font-medium text-slate-700 mb-1">Workflow Preview</div>
        <div className="text-sm text-[var(--text-primary)] font-medium">{cw.name || 'Unnamed'}</div>
        <div className="text-xs text-slate-500 mt-1">Complex workflow — managed externally{cw.workflowId ? ` (ID: ${cw.workflowId})` : ''}</div>
        {cw.url && (
          <div className="text-xs text-blue-600 mt-1 truncate">{cw.url}</div>
        )}
      </div>
    )
  }

  // Builder workflow — show step chain
  const bw = workflow as BuilderWorkflow
  if (bw.steps.length === 0) {
    return (
      <div className="mt-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3">
        <div className="text-xs font-medium text-slate-700 mb-1">Workflow Preview</div>
        <div className="text-xs text-slate-500">No steps configured in this workflow yet.</div>
      </div>
    )
  }

  return (
    <div className="mt-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3">
      <div className="text-xs font-medium text-slate-700 mb-2">Workflow Preview</div>
      <div className="flex flex-wrap items-center gap-1">
        {bw.steps.map((step, idx) => (
          <span key={step.id} className="flex items-center gap-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
              {STEP_TYPE_LABELS[step.type] || step.type}
              {step.type === 'idv_biometrics' && step.requirement === 'optional' ? ' (opt)' : ''}
              {step.type === 'risk' && step.branches.length > 0 ? ` (${step.branches.length} branch${step.branches.length > 1 ? 'es' : ''})` : ''}
            </span>
            {idx < bw.steps.length - 1 && (
              <span className="text-slate-400 text-xs mx-0.5">&rarr;</span>
            )}
          </span>
        ))}
      </div>
      <div className="text-[10px] text-slate-500 mt-1.5">{bw.steps.length} step{bw.steps.length !== 1 ? 's' : ''}</div>
    </div>
  )
}

function PercentageSplitInput({
  fields,
  values,
  onChange,
  disabled,
}: {
  fields: Array<{ key: string; label: string }>
  values: Record<string, number>
  onChange: (key: string, value: number) => void
  disabled: boolean
}) {
  const total = Object.values(values).reduce((sum, v) => sum + (v || 0), 0)

  return (
    <div className="space-y-3">
      {fields.map((field) => (
        <div key={field.key} className="flex items-center gap-3">
          <span className="text-sm text-[var(--text-primary)] w-40 shrink-0">{field.label}</span>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="100"
              value={values[field.key] || 0}
              onChange={(e) => onChange(field.key, Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
              disabled={disabled}
              className="w-20 px-3 py-1.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] text-sm focus:outline-none focus:border-primary disabled:opacity-50"
            />
            <span className="text-sm text-[var(--text-muted)]">%</span>
          </div>
        </div>
      ))}
      <div className={`text-sm font-medium mt-2 ${total === 100 ? 'text-green-500' : total > 100 ? 'text-red-400' : 'text-yellow-500'}`}>
        Total: {total}%{total !== 100 && ' (should be 100%)'}
      </div>
    </div>
  )
}

export function KybConfigTab() {
  const {
    scope,
    updateKybConfig,
    toggleKybAuCheck,
    addKybInternationalCountry,
    removeKybInternationalCountry,
    toggleKybInternationalCountryProduct,
    updateTrustTypeSplit,
  } = useScopeStore()
  const { kybConfig } = scope
  const resellerImported = useHasResellerParent()
  const disabled = scope.finalized || resellerImported
  const isV1 = scope.generalInfo.platformVersion === 'v1'
  const kybAmlMonitoringLocked = useIsResellerLocked('kybConfig', 'kybAmlOngoingMonitoringEnabled')
  const continuousKybLocked = useIsResellerLocked('kybConfig', 'continuousKybEnabled')

  // Local state for new country input
  const [newCountry, setNewCountry] = useState('')

  const tabHeaderGroup = getGroupById('kyb_tab_header')
  const enablementSection = getSectionById('kyb_enablement')
  const enablementGroup = getGroupById('kyb_enablement_group')
  const enabledField = getFieldById('kyb_enabled')

  const auChecksSection = getSectionById('kyb_au_checks')
  const auChecksGroup = getGroupById('kyb_au_checks_group')

  const internationalSection = getSectionById('kyb_international')
  const internationalGroup = getGroupById('kyb_international_group')

  const trustAnalyserEnabledField = getFieldById('kyb_trust_analyser_enabled')
  const onboardingFormEnabledField = getFieldById('kyb_onboarding_form_enabled')
  const onboardingFormBrandedField = getFieldById('kyb_onboarding_form_branded')

  // Style for incomplete required fields — suppressed for upsells
  const { incompleteClass, isUpsell } = useUpsellContext()

  const isEnabledIncomplete = !isUpsell && kybConfig.enabled === 'unknown'
  const isKycForIndividualsIncomplete = !isUpsell && kybConfig.enabled === 'yes' && kybConfig.kycForIndividualsRequired === 'unknown'
  const isAuChecksIncomplete = !isUpsell && kybConfig.enabled === 'yes' && !Object.values(kybConfig.auChecks).some(Boolean)

  const handleAddCountry = () => {
    const trimmed = newCountry.trim()
    if (trimmed) {
      addKybInternationalCountry(trimmed)
      setNewCountry('')
    }
  }

  return (
    <div className="space-y-6">
      <ResellerInheritedBanner />
      {/* Section Header */}
      <div className="border-b border-[var(--border-color)] pb-4">
        <h3 className="text-lg font-medium text-[var(--text-primary)]">
          {tabHeaderGroup?.formTitle || 'KYB Configuration'}
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {tabHeaderGroup?.formDescription || 'Know Your Business verification settings'}
        </p>
      </div>

      {/* Enable KYB */}
      <div className={`border border-[var(--border-color)] rounded-lg p-4 ${isEnabledIncomplete ? incompleteClass : ''}`}>
        <div className="mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--text-primary)]">
              {enabledField?.formLabel || enablementGroup?.formTitle || 'Enable KYB Verification'}
            </span>
            {enabledField?.tooltip && (
              <Tooltip content={enabledField.tooltip}>
                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
              </Tooltip>
            )}
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            <span className="text-primary">*</span>{' '}
            {enabledField?.formDescription ||
              enablementSection?.formDescription ||
              enablementGroup?.helperText ||
              'Business identity verification and compliance checks'}
          </p>
        </div>
        <TriStateRadio
          name="kyb-enabled"
          value={kybConfig.enabled as TriState}
          onChange={(v) => updateKybConfig('enabled', v)}
          disabled={disabled}
        />
      </div>

      {/* Only show these sections if KYB is enabled */}
      {kybConfig.enabled === 'yes' && (
        <>
          {/* ======================== */}
          {/* Australian Business Checks */}
          {/* ======================== */}
          <div className="pt-2">
            <h4 className="text-base font-semibold text-[var(--text-primary)]">
              {auChecksSection?.formTitle || auChecksGroup?.formTitle || 'Australian Business Checks'}
            </h4>
          </div>
          <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
            <div className={`border border-[var(--border-color)] rounded-lg p-4 ${isAuChecksIncomplete ? incompleteClass : ''}`}>
              <p className="text-xs text-[var(--text-muted)] mb-3">
                <span className="text-primary">*</span>{' '}
                {auChecksGroup?.helperText || 'Select the checks required for Australian business verification.'}
              </p>
              <div className="space-y-3">
                {AU_CHECKS.map((check) => {
                  const fieldId = `kyb_au_check_${check.id === 'amlStandard' ? 'aml_standard' : check.id === 'amlEnhanced' ? 'aml_enhanced' : check.id === 'asicInfo' ? 'asic_info' : check.id === 'creditReport' ? 'credit_report' : check.id}`
                  const field = getFieldById(fieldId)
                  return (
                    <div key={check.id} className="flex items-center gap-2">
                      <label className="flex items-center gap-3 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                          checked={kybConfig.auChecks[check.id as keyof typeof kybConfig.auChecks]}
                          onChange={() => toggleKybAuCheck(check.id as keyof typeof kybConfig.auChecks)}
                          disabled={disabled}
                        />
                        <span className="text-sm text-[var(--text-primary)]">{field?.formLabel || check.label}</span>
                      </label>
                      {field?.tooltip && (
                        <Tooltip content={field.tooltip}>
                          <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                        </Tooltip>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* ======================== */}
          {/* Auto KYC/AML on UBO — V1 Only */}
          {/* ======================== */}
          {isV1 && (
            <>
              <div className="pt-2">
                <h4 className="text-base font-semibold text-[var(--text-primary)]">Automatic KYC/AML on UBOs</h4>
              </div>
              <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
                <div className={`border border-[var(--border-color)] rounded-lg p-4 ${isKycForIndividualsIncomplete ? incompleteClass : ''}`}>
                  <div className="mb-3">
                    <p className="text-sm text-[var(--text-secondary)] mb-2">Should KYC and AML checks run automatically when a UBO is identified?</p>
                    <p className="text-xs text-[var(--text-muted)] mb-3">
                      If Yes, KYC and AML checks will be triggered automatically as soon as a beneficial owner is identified through KYB. If No, these checks will need to be initiated as a separate step.
                    </p>
                  </div>
                  <TriStateRadio
                    name="kyc-for-individuals"
                    value={kybConfig.kycForIndividualsRequired}
                    onChange={(v) => updateKybConfig('kycForIndividualsRequired', v)}
                    disabled={disabled}
                  />
                </div>
              </div>
            </>
          )}

          {/* ======================== */}
          {/* UBO Consent & Workflow */}
          {/* ======================== */}
          <div className="pt-2">
            <h4 className="text-base font-semibold text-[var(--text-primary)]">UBO Consent & Workflow</h4>
          </div>
          <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
          <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-5">
            <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <InfoIcon size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-[var(--text-primary)]">
                <p className="mb-1">
                  Running KYC on UBOs identified via KYB requires <strong>explicit consent</strong> from the individual.
                </p>
                <p className="text-[var(--text-muted)]">
                  If the client wants to send the hosted URL via SMS, they will need to have the entity's phone number available.
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-2">
                Is the client aware that consent is required to run KYC on UBOs?
              </p>
              <TriStateRadio
                name="ubo-consent-aware"
                value={kybConfig.uboConsentAware}
                onChange={(v) => updateKybConfig('uboConsentAware', v)}
                disabled={disabled}
              />
            </div>

            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-2">
                Is the client aware that SMS delivery requires the entity's phone number?
              </p>
              <TriStateRadio
                name="ubo-sms-phone-aware"
                value={kybConfig.uboSmsPhoneAware}
                onChange={(v) => updateKybConfig('uboSmsPhoneAware', v)}
                disabled={disabled}
              />
            </div>

            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-2">
                Which workflow should be run on UBOs?
              </p>
              <select
                className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={kybConfig.uboWorkflowId}
                onChange={(e) => updateKybConfig('uboWorkflowId', e.target.value)}
                disabled={disabled}
              >
                <option value="">— Not selected —</option>
                {scope.workflowsConfig.map((wf) => (
                  <option key={wf.id} value={wf.id}>
                    {wf.name || `Workflow ${wf.id}`}
                  </option>
                ))}
              </select>
              {scope.workflowsConfig.length === 0 && (
                <p className="text-xs text-yellow-500 mt-1">
                  No workflows configured yet. Add workflows in the Workflows tab first.
                </p>
              )}
              {kybConfig.uboWorkflowId && (
                <WorkflowPreview workflowId={kybConfig.uboWorkflowId} workflows={scope.workflowsConfig} />
              )}
            </div>
          </div>
          </div>


          {/* ======================== */}
          {/* International KYB — Per-Country Products */}
          {/* ======================== */}
          <div className="pt-2">
            <h4 className="text-base font-semibold text-[var(--text-primary)]">
              {internationalSection?.formTitle || internationalGroup?.formTitle || 'International KYB'}
            </h4>
          </div>
          <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
          <div className="border border-[var(--border-color)] rounded-lg p-4">
            <div className="mb-4">
              <p className="text-sm text-[var(--text-secondary)] mb-2">Require international KYB verification?</p>
              <TriStateRadio
                name="international-enabled"
                value={kybConfig.internationalEnabled}
                onChange={(v) => updateKybConfig('internationalEnabled', v)}
                disabled={disabled}
              />
            </div>

            {kybConfig.internationalEnabled === 'yes' && (
              <>
                {/* Add country input */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    className="flex-1 h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder="Enter country name"
                    value={newCountry}
                    onChange={(e) => setNewCountry(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCountry() } }}
                    disabled={disabled}
                  />
                  <button
                    type="button"
                    onClick={handleAddCountry}
                    disabled={disabled || !newCountry.trim()}
                    className="flex items-center gap-1 px-3 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>

                {/* Country list with per-country product selection */}
                {kybConfig.internationalCountryProducts.length === 0 ? (
                  <p className="text-xs text-[var(--text-muted)]">No countries added yet.</p>
                ) : (
                  <div className="space-y-3">
                    {kybConfig.internationalCountryProducts.map((cp) => (
                      <div key={cp.country} className="border border-[var(--border-color)] rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-[var(--text-primary)]">{cp.country}</span>
                          <button
                            type="button"
                            onClick={() => removeKybInternationalCountry(cp.country)}
                            disabled={disabled}
                            className="text-red-400 hover:text-red-300 disabled:opacity-50"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          {INTERNATIONAL_KYB_PRODUCTS.map((product) => (
                            <label key={product.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                                checked={cp.products.includes(product.id)}
                                onChange={() => toggleKybInternationalCountryProduct(cp.country, product.id)}
                                disabled={disabled}
                              />
                              <span className="text-xs text-[var(--text-primary)]">{product.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          </div>

          {/* ======================== */}
          {/* Trust Analyser — Percentage Split */}
          {/* ======================== */}
          <div className="border border-[var(--border-color)] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-medium text-[var(--text-primary)]">
                {trustAnalyserEnabledField?.formLabel || 'Trust Analyser'}
              </h4>
              {trustAnalyserEnabledField?.tooltip && (
                <Tooltip content={trustAnalyserEnabledField.tooltip}>
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </Tooltip>
              )}
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1 mb-3">
              {trustAnalyserEnabledField?.formDescription || 'Trust verification requirements and supported trust types.'}
            </p>
            <TriStateRadio
              name="trust-analyser-enabled"
              value={kybConfig.trustAnalyserEnabled}
              onChange={(v) => updateKybConfig('trustAnalyserEnabled', v)}
              disabled={disabled}
            />

            {kybConfig.trustAnalyserEnabled === 'yes' && (
              <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
                <p className="text-xs text-[var(--text-muted)] mb-3">
                  Enter the approximate percentage split of trust types. The three supported types are Unit, Discretionary, and SMSF — everything else falls under "Other".
                </p>
                <PercentageSplitInput
                  fields={TRUST_TYPE_SPLIT_FIELDS}
                  values={kybConfig.trustTypeSplit as unknown as Record<string, number>}
                  onChange={(key, value) => updateTrustTypeSplit(key as keyof typeof kybConfig.trustTypeSplit, value)}
                  disabled={disabled}
                />
              </div>
            )}
          </div>

          {/* ======================== */}
          {/* KYB Onboarding Form */}
          {/* ======================== */}
          <div className="border border-[var(--border-color)] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-medium text-[var(--text-primary)]">
                {onboardingFormEnabledField?.formLabel || 'KYB Onboarding Form'}
              </h4>
              {onboardingFormEnabledField?.tooltip && (
                <Tooltip content={onboardingFormEnabledField.tooltip}>
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </Tooltip>
              )}
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1 mb-3">
              {onboardingFormEnabledField?.formDescription || 'KYB onboarding form configuration and branding.'}
            </p>
            <TriStateRadio
              name="onboarding-form-enabled"
              value={kybConfig.onboardingFormEnabled}
              onChange={(v) => updateKybConfig('onboardingFormEnabled', v)}
              disabled={disabled}
            />

            {kybConfig.onboardingFormEnabled === 'yes' && (
              <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
                <div className="flex items-center gap-2 mb-3">
                  <h5 className="text-sm text-[var(--text-secondary)]">
                    {onboardingFormBrandedField?.formLabel || 'Branded KYB Form'}
                  </h5>
                  {onboardingFormBrandedField?.tooltip && (
                    <Tooltip content={onboardingFormBrandedField.tooltip}>
                      <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                    </Tooltip>
                  )}
                </div>
                <p className="text-xs text-[var(--text-muted)] mb-3">
                  {onboardingFormBrandedField?.formDescription || 'Whether the onboarding form will be branded with customer logo and colors.'}
                </p>
                <TriStateRadio
                  name="onboarding-form-branded"
                  value={kybConfig.onboardingFormBranded}
                  onChange={(v) => updateKybConfig('onboardingFormBranded', v)}
                  disabled={disabled}
                />
              </div>
            )}
          </div>

          {/* ======================== */}
          {/* KYB AML Ongoing Monitoring */}
          {/* ======================== */}
          {(() => {
            const amlLevel = scope.amlConfig.level
            const amlVendor = scope.amlConfig.vendor
            const isRefinitiv = amlVendor === 'refinitiv'
            const isComply = amlVendor === 'comply'
            const hasAdverseMedia = amlLevel === 'enhanced' || kybConfig.auChecks.amlEnhanced
            const isStandard = amlLevel === 'standard' || kybConfig.auChecks.amlStandard
            const amlNotConfigured = !amlLevel || amlLevel === 'unknown' || amlLevel === 'na'

            const coverageText = hasAdverseMedia
              ? 'sanctions and adverse media'
              : isStandard
              ? 'sanctions'
              : 'sanctions (and adverse media if AML Enhanced is selected)'

            const levelBadge = hasAdverseMedia
              ? { label: 'Enhanced', className: 'bg-blue-50 text-blue-700 border border-blue-200' }
              : isStandard
              ? { label: 'Standard', className: 'bg-gray-50 text-gray-700 border border-gray-200' }
              : null

            return (
              <>
                <div className="pt-2">
                  <div className="flex items-center gap-3">
                    <h4 className="text-base font-semibold text-[var(--text-primary)]">KYB AML Ongoing Monitoring</h4>
                    {levelBadge && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${levelBadge.className}`}>
                        {levelBadge.label}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                    Continuous screening of business entities after onboarding to detect {coverageText} hits that could indicate increased money-laundering, fraud, or reputational risk. PEP screening does not apply to business entities.
                  </p>
                </div>
                <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
                  <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-4">
                    {/* AML config summary */}
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-[var(--text-secondary)]">AML Provider:</span>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        isRefinitiv ? 'bg-purple-50 text-purple-700 border border-purple-200'
                        : isComply ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : amlVendor && amlVendor !== 'unknown' ? 'bg-gray-50 text-gray-700 border border-gray-200'
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        {isRefinitiv ? 'Refinitiv (World-Check)' : isComply ? 'ComplyAdvantage' : amlVendor === 'other' ? (scope.amlConfig.otherVendor || 'Other') : 'Not set — configure in AML tab'}
                      </span>
                      <span className="text-[var(--text-muted)]">|</span>
                      <span className="text-[var(--text-secondary)]">Monitoring covers:</span>
                      <span className="text-xs text-[var(--text-primary)] font-medium">
                        Sanctions{hasAdverseMedia ? ' + Adverse Media' : ''}
                      </span>
                    </div>

                    {amlNotConfigured && (
                      <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                        <InfoIcon size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-[var(--text-primary)]">
                          AML level has not been configured yet. Set it in the <strong>AML tab</strong> to determine whether ongoing monitoring includes adverse media (Enhanced) or sanctions only (Standard).
                        </p>
                      </div>
                    )}

                    {/* Provider-specific notes */}
                    {isRefinitiv && (
                      <div className="flex items-start gap-3 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                        <InfoIcon size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-[var(--text-primary)] space-y-1">
                          <p><strong>Refinitiv (World-Check)</strong> — ongoing monitoring available via API and (with some limitations) Portal for organisation checks.</p>
                          <p className="text-[var(--text-secondary)]">Billing: requires per-entity registration with separate billing/contract items.</p>
                        </div>
                      </div>
                    )}

                    {isComply && (
                      <div className="flex items-start gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                        <InfoIcon size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-[var(--text-primary)]">
                          <strong>ComplyAdvantage</strong> — event-driven ongoing monitoring. Notifies only when there is a hit or change.
                        </p>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-[var(--text-primary)]">Enable KYB AML Ongoing Monitoring</span>
                        <ResellerLockBadge section="kybConfig" field="kybAmlOngoingMonitoringEnabled" />
                        <Tooltip content={`Continuously screens business entities against ${coverageText} after onboarding. Alerts are delivered when a new hit or change is detected. PEP screening is not applicable to business entities.`}>
                          <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                        </Tooltip>
                      </div>
                    <TriStateRadio
                      name="kyb-aml-ongoing-monitoring"
                      value={kybConfig.kybAmlOngoingMonitoringEnabled}
                      onChange={(v) => updateKybConfig('kybAmlOngoingMonitoringEnabled', v)}
                      disabled={disabled || kybAmlMonitoringLocked}
                    />
                    </div>

                    {kybConfig.kybAmlOngoingMonitoringEnabled === 'yes' && (
                      <div className="pt-2 border-t border-[var(--border-color)]">
                        <label className="block text-sm text-[var(--text-secondary)] mb-2">Notes</label>
                        <textarea
                          className="w-full min-h-[80px] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] text-sm focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
                          placeholder="e.g., Specific monitoring requirements, notification preferences..."
                          value={kybConfig.kybAmlOngoingMonitoringNotes}
                          onChange={(e) => updateKybConfig('kybAmlOngoingMonitoringNotes', e.target.value)}
                          disabled={disabled}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )
          })()}

          {/* ======================== */}
          {/* Continuous KYB Monitoring */}
          {/* ======================== */}
          <>
              <div className="pt-2">
                <div className="flex items-center gap-3">
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">Continuous KYB Monitoring</h4>
                  {!isV1 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-500 border border-amber-500/30">
                      V2: Live mid-May 2026
                    </span>
                  )}
                </div>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                  Continuous KYB (cKYB) monitors a business after initial onboarding and sends alerts when important changes occur — ownership, registration status, adverse events, credit issues, etc. Uses <strong>CreditorWatch</strong> as the AU data provider; FrankieOne polls daily and delivers notifications for subscribed entities (UBO/ownership, ASIC/ABR status, insolvency/credit events, RiskScore changes).
                </p>
              </div>
              <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
                <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-[var(--text-primary)]">Enable Continuous KYB Monitoring</span>
                      <ResellerLockBadge section="kybConfig" field="continuousKybEnabled" />
                    </div>
                    <TriStateRadio
                      name="continuous-kyb-enabled"
                      value={kybConfig.continuousKybEnabled}
                      onChange={(v) => updateKybConfig('continuousKybEnabled', v)}
                      disabled={disabled || continuousKybLocked}
                    />
                  </div>

                  {kybConfig.continuousKybEnabled === 'yes' && (
                    <div className="pt-2 border-t border-[var(--border-color)]">
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">Notes</label>
                      <textarea
                        className="w-full min-h-[80px] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] text-sm focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
                        placeholder="e.g., Specific alert types needed, notification preferences, volume estimates..."
                        value={kybConfig.continuousKybNotes}
                        onChange={(e) => updateKybConfig('continuousKybNotes', e.target.value)}
                        disabled={disabled}
                      />
                    </div>
                  )}
                </div>
              </div>
          </>

        </>
      )}
    </div>
  )
}
